import { attachVoiceInputs } from '@/lib/voice/VoiceInput';
import { esc } from './dom';
import { VOICE_PENDING_PROMPT, VOICE_PENDING_SAVE_PROMPT } from '@/lib/voice/session';
import {
  readModalValues, hasUnsavedChanges, DISCARD_PROMPT, saveErrorMessage, type ModalValues,
} from './modal-dirty';

export interface FieldDef {
  name: string;
  label: string;
  type?: 'text' | 'textarea' | 'number' | 'date' | 'color' | 'checkbox' | 'select' | 'email' | 'tel';
  required?: boolean;
  options?: { value: string; label: string }[];
  defaultValue?: string | number | boolean | null;
  placeholder?: string;
  min?: number;
  max?: number;
  /**
   * Offer dictation on this field. Textareas only — a mic on a one-line input
   * invites a sentence into a field sized for a few words.
   */
  voice?: boolean;
  /**
   * What the tidy pass should make of this field once dictation stops
   * (2026-09-30). 'line' never breaks a line — a punch item is one thought,
   * and a bullet in a table cell reads as a bug. 'scope' is client-facing and
   * prefers prose. Defaults to 'notes', which may make a list out of a list
   * but never invents one.
   */
  voiceKind?: 'line' | 'notes' | 'scope';
}

export interface ModalConfig {
  title: string;
  fields: FieldDef[];
  submitLabel?: string;
  showDelete?: boolean;
  /**
   * Wording for the destructive button. Defaults to "Delete".
   *
   * Worth setting whenever Delete does not mean "delete the thing": the
   * contact modal's button removes someone from ONE project in one mode and
   * deletes them from every project in the other, and a button labelled the
   * same in both cases is how the wrong one gets pressed.
   */
  deleteLabel?: string;
  /**
   * What the confirm asks before Delete runs. Defaults to "Delete this item?
   * This cannot be undone."
   *
   * Set it whenever the caller can say something more useful than that — a
   * contact's delete, for instance, knows how many projects, portal links and
   * tasks it is about to take with it, and a generic question can't be
   * answered by anyone who doesn't already know that.
   */
  deleteConfirm?: string;
  onSubmit: (values: Record<string, string | boolean>) => Promise<void>;
  onDelete?: () => Promise<void>;
}

export function openModal(cfg: ModalConfig): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal';

  const fieldsHtml = cfg.fields.map((f) => {
    const id = `f-${f.name}`;
    const dv = f.defaultValue;
    const requiredAttr = f.required ? 'required' : '';
    const placeholder = f.placeholder ? `placeholder="${esc(f.placeholder)}"` : '';
    if (f.type === 'textarea') {
      const voiceAttr = f.voice
        ? `data-voice="1"${f.voiceKind ? ` data-voice-kind="${esc(f.voiceKind)}"` : ''}`
        : '';
      return `<label class="field"><span>${esc(f.label)}</span>
        <textarea id="${id}" name="${esc(f.name)}" rows="3" ${requiredAttr} ${placeholder} ${voiceAttr}>${esc(dv == null ? '' : String(dv))}</textarea></label>`;
    }
    if (f.type === 'checkbox') {
      const checked = dv ? 'checked' : '';
      return `<label class="field-checkbox"><input type="checkbox" id="${id}" name="${esc(f.name)}" ${checked} /><span>${esc(f.label)}</span></label>`;
    }
    if (f.type === 'select') {
      const opts = (f.options ?? []).map((o) =>
        `<option value="${esc(o.value)}" ${o.value === String(dv ?? '') ? 'selected' : ''}>${esc(o.label)}</option>`
      ).join('');
      return `<label class="field"><span>${esc(f.label)}</span>
        <select id="${id}" name="${esc(f.name)}" ${requiredAttr}>${opts}</select></label>`;
    }
    const inputType = f.type ?? 'text';
    const min = f.min != null ? `min="${f.min}"` : '';
    const max = f.max != null ? `max="${f.max}"` : '';
    return `<label class="field"><span>${esc(f.label)}</span>
      <input type="${inputType}" id="${id}" name="${esc(f.name)}" value="${esc(dv == null ? '' : String(dv))}" ${requiredAttr} ${placeholder} ${min} ${max} /></label>`;
  }).join('');

  overlay.innerHTML = `
    <form class="modal-card" novalidate>
      <h2>${esc(cfg.title)}</h2>
      ${fieldsHtml}
      <div class="modal-error" hidden></div>
      <div class="modal-actions">
        ${cfg.showDelete ? `<button type="button" class="btn-danger" data-action="delete">${esc(cfg.deleteLabel ?? 'Delete')}</button>` : ''}
        <span style="flex:1"></span>
        <button type="button" class="btn-link" data-action="cancel">Cancel</button>
        <button type="submit" class="btn-primary">${esc(cfg.submitLabel ?? 'Save')}</button>
      </div>
    </form>
  `;

  const form = overlay.querySelector<HTMLFormElement>('form')!;
  const read = (): ModalValues => {
    const fd = new FormData(form);
    return readModalValues(cfg.fields, (n) => fd.get(n));
  };

  // Set after the overlay is live, from the RENDERED form rather than
  // cfg.defaultValue: a select whose default matches no option shows its first
  // option, and comparing against the config would then report an untouched
  // modal as edited.
  let initial: ModalValues = {};
  let stopVoice = (): void => {};

  const onKey = (e: KeyboardEvent): void => {
    // The listener lives on `document`, so it outlives the overlay if anything
    // removes the overlay without going through close(). Unguarded, that left
    // a ghost handler that answered every later Escape on the page by asking
    // "Discard your unsaved changes?" about a dialog no longer on screen —
    // found by tests/lib/dom-modal.test.ts, where exactly that happened. A
    // detached overlay cleans up after itself instead.
    if (!overlay.isConnected) {
      document.removeEventListener('keydown', onKey);
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); requestClose(); }
  };

  /** Close unconditionally — after a save or a delete, nothing is lost. */
  const close = (): void => {
    document.removeEventListener('keydown', onKey);
    // End any dictation first. Removing the overlay does not stop a recorder,
    // so without this the microphone stayed live behind a closed dialog.
    stopVoice();
    overlay.remove();
  };

  /**
   * Close in response to a user dismissing the modal. Asks first when that
   * would throw away typed or dictated content (see ./modal-dirty).
   */
  const requestClose = (): void => {
    // Dictation still waiting to become text (offline, or a clip being
    // retried) isn't in the form yet, so the unsaved-changes check can't see
    // it. The mic marks its field instead (VoiceInput.ts, markPending).
    if (voicePending()) {
      if (!confirm(VOICE_PENDING_PROMPT)) return;
    } else if (hasUnsavedChanges(initial, read()) && !confirm(DISCARD_PROMPT)) {
      return;
    }
    close();
  };

  const voicePending = (): boolean => overlay.querySelector('[data-voice-pending]') !== null;

  overlay.addEventListener('click', (e) => { if (e.target === overlay) requestClose(); });
  overlay.querySelector('[data-action="cancel"]')!.addEventListener('click', requestClose);
  if (cfg.onDelete) {
    overlay.querySelector('[data-action="delete"]')!.addEventListener('click', async () => {
      if (!confirm(cfg.deleteConfirm ?? 'Delete this item? This cannot be undone.')) return;
      try { await cfg.onDelete!(); close(); }
      catch (err) { showErr(overlay, err); }
    });
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (voicePending() && !confirm(VOICE_PENDING_SAVE_PROMPT)) return;
    // Same reader as the snapshot, so "unchanged" means the same thing to both.
    try { await cfg.onSubmit(read()); close(); }
    catch (err) { showErr(overlay, err); }
  });

  document.body.append(overlay);
  // Only once the overlay is live. onKey treats a detached overlay as dead and
  // unhooks itself, so listening any earlier would make that guard's meaning
  // depend on there being no event-loop turn between here and the append.
  document.addEventListener('keydown', onKey);
  // After append: the mic button measures and mounts against a live element.
  stopVoice = attachVoiceInputs(overlay);
  initial = read();
  overlay.querySelector<HTMLInputElement>('input,textarea,select')?.focus();
}

function showErr(overlay: HTMLElement, err: unknown): void {
  const box = overlay.querySelector<HTMLDivElement>('.modal-error')!;
  box.hidden = false;
  box.textContent = saveErrorMessage(err, navigator.onLine !== false);
}
